import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { adminResetPassword } from "@/lib/api";
import { adminInputCls, adminGoldBtnCls } from "@/components/admin/shared";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await adminResetPassword({ token, new_password: next });
      setDone(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Reset failed — the link may have expired");
    }
    setBusy(false);
  };

  return (
    <main data-testid="reset-password-page" className="flex min-h-screen items-center justify-center bg-ink px-5">
      <div className="w-full max-w-sm border border-white/10 bg-[#0d0d0d] p-8">
        <p className="font-display text-2xl font-black uppercase tracking-tight text-white">
          R3Stan <span className="text-gold">Admin</span>
        </p>
        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
          Choose a new password
        </p>

        {done ? (
          <div data-testid="reset-success" className="mt-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.5} />
            <p className="mt-5 text-sm leading-relaxed text-neutral-300">
              Your password has been updated. You can sign in with it now.
            </p>
            <Link
              data-testid="reset-login-link"
              to="/admin"
              className={`${adminGoldBtnCls} mt-6 w-full`}
            >
              Go to Admin Login
            </Link>
          </div>
        ) : (
          <form data-testid="reset-form" onSubmit={submit} className="mt-8 space-y-4">
            <input
              data-testid="reset-new-input"
              type="password"
              required
              placeholder="New password (8+ characters)"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={adminInputCls}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={adminInputCls}
            />
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={busy}
              className={`${adminGoldBtnCls} w-full`}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Set New Password
            </button>
            <p className="text-center text-[11px] leading-relaxed text-neutral-600">
              This link works once and expires one hour after it was created.
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
